import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { PartnerReview, PartnerReviewContext } from '@/types/conversation'

export const partnerReviewKeys = {
  all: ['partner-review'] as const,
  detail: (conversationId: string | number) =>
    ['partner-review', String(conversationId)] as const,
}

export interface SubmitPartnerReviewInput {
  messageIndex: number
  flagged: boolean
  comment: string
}

/**
 * Hook to fetch the partner review context for a conversation (admin only)
 */
export function usePartnerReview(
  conversationId: string | number | undefined,
  { enabled = true }: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: partnerReviewKeys.detail(conversationId!),
    queryFn: async () => {
      const response = await api.get<PartnerReviewContext>(
        `/conversations/${conversationId}/partner-review`
      )
      return response.data
    },
    enabled: Boolean(conversationId) && enabled,
  })
}

/**
 * Hook to submit one turn review; the backend forwards it to the partner API
 */
export function useSubmitPartnerReview(
  conversationId: string | number | undefined
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: SubmitPartnerReviewInput) => {
      const response = await api.post<PartnerReview>(
        `/conversations/${conversationId}/partner-review`,
        {
          message_index: input.messageIndex,
          flagged: input.flagged,
          comment: input.comment,
        }
      )
      return response.data
    },
    onSuccess: () => {
      if (conversationId) {
        queryClient.invalidateQueries({
          queryKey: partnerReviewKeys.detail(conversationId),
        })
      }
    },
  })
}
